"use client";

import { useEffect } from "react";

/**
 * Restores scroll position when the user navigates back to a product listing
 * from a product detail page.
 *
 * How it works:
 *   1. A capture-phase click listener saves window.scrollY to sessionStorage
 *      any time a link to /shop/product/* is clicked, keyed to the listing's
 *      pathname (so /shop/category/flower and /shop/all don't collide).
 *   2. Once `ready` flips true (products loaded, so the page has real height),
 *      we read that saved position back and jump to it, then clear the key so
 *      it only fires once per round-trip.
 *
 * Pass `ready` = "products are loaded and rendered" — usually `!loading`.
 *
 * Drop-in for any listing page (category, all, brand, collection, search,
 * specials). Safe to call on pages with or without pagination.
 *
 * Pass the current `page` number too (for paginated listings) so the page is
 * mirrored into ?page= and a back-nav returns to the same page, not page 1.
 */

/**
 * Initial page for a paginated listing, read from ?page=. Use as a useState
 * initializer: `useState(initialPageFromUrl)`.
 */
export function initialPageFromUrl(): number {
  if (typeof window === "undefined") return 1;
  const p = new URLSearchParams(window.location.search).get("page");
  const n = p ? parseInt(p, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function useListingScrollRestore(ready: boolean, page?: number) {
  // Capture scroll on any product-link click.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      const href = anchor?.getAttribute("href") || "";
      if (href.startsWith("/shop/product/")) {
        sessionStorage.setItem(
          `listing-scroll:${window.location.pathname}`,
          String(window.scrollY)
        );
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  // Restore once the listing is ready.
  useEffect(() => {
    if (!ready || typeof window === "undefined") return;
    const key = `listing-scroll:${window.location.pathname}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return;
    const y = parseInt(stored, 10);
    sessionStorage.removeItem(key);
    if (Number.isFinite(y) && y > 0) {
      // Two frames so React finishes painting the grid before we jump.
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          window.scrollTo({ top: y, behavior: "instant" as ScrollBehavior })
        )
      );
    }
  }, [ready]);

  // Mirror the current page into ?page= so back-nav returns to the same page.
  useEffect(() => {
    if (page === undefined || typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (page === 1) url.searchParams.delete("page");
    else url.searchParams.set("page", String(page));
    window.history.replaceState({}, "", url.toString());
  }, [page]);
}
